/**
 * The one sentence at the top of a refusal — and the one place it is chosen.
 *
 * Bundle 2 item 2. Most codes are a fixed phrase: the guardrail that fired is
 * the whole story, and `HEADLINES` maps it. `ATTEST_EVIDENCE_NOT_VERIFIED` is
 * not, and that is what this module exists for.
 *
 * That one code covers three different situations with three different remedies,
 * and it used to render one sentence for all of them: *"a source document does
 * not support the statement it is attached to"*. When nothing was attached at
 * all, that sentence sent the CPA to re-read a document they never uploaded —
 * the single most common way this screen wasted somebody's afternoon. So the
 * headline reads the findings and names the actual cause:
 *
 * - **contradiction** — a document is there and it disagrees. Go and look at it.
 * - **absence** — nothing is attached, or the document is simply silent on the
 *   figure, the period or the entity. Attach something (or, for the tier that
 *   allows it, declare that the records are in the firm's file). Nothing to
 *   re-read.
 * - **unreadable** — a file arrived and no text came out of it: an image-only
 *   scan, an encrypted PDF, an export nothing can parse. Attach a readable copy.
 *
 * The buckets are the engine's own codes, grouped, not re-derived: every name
 * below is a constant in `core/attest/evidence_findings.py`, and the per-finding
 * prose the CPA reads underneath is still the server's `reason`, verbatim. This
 * chooses which of three true sentences to lead with; it never decides anything.
 */
import type { AttestRefusal, EvidenceNotVerifiedDetail } from '@/components/attest/api'

/** Headline per guardrail. Plain language first; the code is secondary. */
const HEADLINES: Record<string, string> = {
  ATTEST_REFUSED: 'This letter cannot be issued as asked',
  ATTEST_POLICY_INVALID: 'Letters are unavailable — the assertion policy did not load',
  ATTEST_ASSERTION_DENIED: 'The letter contains an assertion a CPA may not make',
  ATTEST_ASSURANCE_EXCEEDED: 'The wording conveys more assurance than this letter supports',
  ATTEST_ENGAGEMENT_REFUSED: 'No permissible engagement covers this request',
  ATTEST_INDEPENDENCE_IMPAIRED: 'This firm is not independent of the subject entity',
  ATTEST_ISSUER_NOT_ELIGIBLE: 'This firm is not eligible to perform this engagement',
  // 403 from the issuer gate, like the line above it — "this firm may not
  // sign", not "your request is wrong". The headline says whose problem it is.
  ATTEST_FIRM_PROFILE_MISSING: 'This firm has no active profile to sign the letter under',
  ATTEST_EVIDENCE_MISSING: 'A factual statement has no source behind it',
  // Only reached when the findings do not classify — see `evidenceCause`. It
  // deliberately claims nothing about whether a document exists.
  ATTEST_EVIDENCE_NOT_VERIFIED: 'The evidence behind this letter could not be verified',
  ATTEST_LETTER_TYPE_UNKNOWN: 'That letter type is not offered by this deployment',
  ATTEST_INPUT_MISSING: 'A fact this letter type requires was not supplied',
}

export type EvidenceCause = 'contradiction' | 'absence' | 'unreadable'

/**
 * A document is present and it does not line up.
 *
 * `WRONG_DOCUMENT` belongs here rather than with absence even though one of its
 * three triggers is "the kind could not be established": in all three a file was
 * attached and read, so pointing the CPA at it is never the wrong instruction.
 */
const CONTRADICTION: ReadonlySet<string> = new Set([
  'WRONG_DOCUMENT',
  'ENTITY_MISMATCH',
  'PERIOD_OUT_OF_SCOPE',
  'FIGURE_NOT_FOUND',
  'PREPARER_MISMATCH',
])

/** A file arrived; no readable content came out of it. */
const UNREADABLE: ReadonlySet<string> = new Set([
  'UNREADABLE_DOCUMENT',
  'FIGURES_UNREADABLE',
])

/**
 * Nothing supports the statement — either nothing was attached, or what was
 * attached says nothing on the point.
 *
 * The `*_UNSTATED` codes are review findings rather than blocks, and they are
 * here for the same reason as the rest: what the CPA has to do about them is
 * supply the missing fact, not re-examine a contradiction that does not exist.
 */
const ABSENCE: ReadonlySet<string> = new Set([
  'NO_DOCUMENT',
  'NO_SUPPORTING_RECORD',
  'UNVERIFIED_REFERENCE',
  'KIND_UNCONFIRMED',
  'ENTITY_UNSTATED',
  'PERIOD_UNSTATED',
  'PREPARER_UNSTATED',
])

const EVIDENCE_HEADLINES: Record<EvidenceCause, string> = {
  contradiction: 'An attached document contradicts the statement it supports',
  absence: 'A statement has nothing on file that supports it',
  unreadable: 'An attached document could not be read, so nothing in it could be checked',
}

/**
 * Which of the three a refusal is about, or `null` when its findings say
 * nothing this recognises — an older backend, a code added since this shipped.
 *
 * Precedence is contradiction → unreadable → absence, and it is a precedence
 * rather than a count: one document that disagrees is the sharpest fact on the
 * screen even in a letter whose other statements are merely unsupported, and it
 * is the one the CPA should be looking at first. Every cause named is still
 * listed finding-by-finding in the detail below the headline, so leading with
 * one never hides the others.
 *
 * Unknown codes return `null` rather than falling into a bucket by default. A
 * guess here reads as the engine's own diagnosis and would be indistinguishable
 * from one.
 */
export function evidenceCause(detail: EvidenceNotVerifiedDetail): EvidenceCause | null {
  const codes = [
    ...(detail.evidence_findings ?? []).map(f => f.code),
    ...(detail.needs_review ?? []).map(f => f.code),
    ...(detail.unverified_statements ?? []).map(s => s.match_status),
  ].filter(Boolean)

  if (codes.some(c => CONTRADICTION.has(c))) return 'contradiction'
  if (codes.some(c => UNREADABLE.has(c))) return 'unreadable'
  if (codes.some(c => ABSENCE.has(c))) return 'absence'
  return null
}

/** The headline for this refusal. Never empty — an unmapped code still gets one. */
export function headlineFor(refusal: AttestRefusal): string {
  if (refusal.error_code === 'ATTEST_EVIDENCE_NOT_VERIFIED') {
    const cause = evidenceCause(refusal.detail as EvidenceNotVerifiedDetail)
    if (cause) return EVIDENCE_HEADLINES[cause]
  }
  return HEADLINES[refusal.error_code] ?? 'This request was refused'
}
